import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import useCapabilities, { useCanViewGrpEnrollmentsBool } from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { GrpEnrollmentStatus } from "@/pages/billing/finance/grp/enrollments/types";
import GrpPersonBenefitsView from "./_components/GrpPersonBenefitsView";
import { findPersonGrpEnrollment, isTruncatedPersonGrpEnrollmentLookup } from "./_components/matchPersonGrpEnrollment";
import { GRP_PERSON_PROFILE_FLAG } from "./constants";
import { PERSON_GRP_ENROLLMENT_QUERY } from "./queries";

export default function RetirementPersonGrpBenefits() {
  const { pgaId } = useParams();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewGrpEnrollments = useCanViewGrpEnrollmentsBool();
  const flagOn = !!flags[GRP_PERSON_PROFILE_FLAG];
  const canQuery = Boolean(pgaId && flagsLoaded && capabilitiesLoaded && flagOn && canViewGrpEnrollments);

  const { data, loading, error, refetch } = useQuery(PERSON_GRP_ENROLLMENT_QUERY, {
    variables: {
      first: 20,
      filters: {
        search: pgaId,
        status: GrpEnrollmentStatus.ACTIVE
      }
    },
    skip: !canQuery
  });

  if (!pgaId || !flagOn || !canViewGrpEnrollments) return null;

  if (!flagsLoaded || !capabilitiesLoaded) {
    return <GrpPersonBenefitsView status="loading" />;
  }

  if (error) {
    return <GrpPersonBenefitsView status="error" errorMessage={error.message} onRetry={() => void refetch()} />;
  }

  if (loading) {
    return <GrpPersonBenefitsView status="loading" />;
  }

  const enrollment = findPersonGrpEnrollment(data?.grpEnrollments?.nodes, pgaId);
  if (isTruncatedPersonGrpEnrollmentLookup(enrollment, data?.grpEnrollments?.pageInfo.hasNextPage ?? false)) {
    return (
      <GrpPersonBenefitsView
        status="error"
        errorMessage="The enrollment search returned more than one page of results, so we cannot confirm this person is not enrolled."
        onRetry={() => void refetch()}
      />
    );
  }

  if (!enrollment) {
    return <GrpPersonBenefitsView status="not-enrolled" />;
  }

  return <GrpPersonBenefitsView status="enrolled" accountOpenedOn={enrollment.accountOpenedOn} />;
}
